import { useCallback, useRef, useState } from "react";
import { Box } from "@mui/material";
import {
  motion,
  useMotionValueEvent,
  useReducedMotion,
  useScroll,
  useTransform,
} from "motion/react";
import { landingColors } from "../../landingColors";
import { flatSteps, groupStartIndex } from "../../data/buyerSellerStepsData";
import StepsTabToggle from "./StepsTabToggle";
import StepDeckCard from "./StepDeckCard";

// Card width as a share of the deck's width. The remainder is split evenly as
// leading/trailing padding, which does two things: it lets the first and last
// cards centre themselves under `scroll-snap-align: center`, and it makes the
// deck's scroll progress map exactly linearly onto card index (card i is
// centred at progress i/(n-1)) — which is what StepDeckCard's offset maths
// assumes.
const CARD_WIDTH_PERCENT = 82;
const EDGE_PAD_PERCENT = (100 - CARD_WIDTH_PERCENT) / 2;

// The desktop sticky sequence is deliberately not ported here. A pinned phone
// plus copy needs more vertical room than a phone viewport has, six steps of
// pinned scroll reads as a frozen page on touch, and iOS Safari's collapsing
// toolbar makes long sticky sections jitter. A horizontal snap deck keeps the
// same content model — six ordered steps, tab-switchable, auto-advancing
// across groups — but spends a swipe instead of the page's vertical scroll.
const MobileStepDeck = () => {
  const prefersReducedMotion = useReducedMotion();
  const scrollRef = useRef(null);
  const [activeIndex, setActiveIndex] = useState(0);

  const { scrollXProgress } = useScroll({ container: scrollRef });

  const lastIndex = Math.max(flatSteps.length - 1, 1);
  useMotionValueEvent(scrollXProgress, "change", (value) => {
    setActiveIndex(Math.round(value * lastIndex));
  });

  const railScaleX = useTransform(
    scrollXProgress,
    [0, 1],
    [1 / flatSteps.length, 1],
  );

  const scrollToIndex = useCallback(
    (index) => {
      const container = scrollRef.current;
      const card = container?.children?.[index];
      if (!card) return;
      // `scrollIntoView` would scroll the page vertically as well as the deck
      // horizontally. Drive the container's own scroll offset instead.
      container.scrollTo({
        left: card.offsetLeft - (container.clientWidth - card.clientWidth) / 2,
        behavior: prefersReducedMotion ? "auto" : "smooth",
      });
    },
    [prefersReducedMotion],
  );

  const activeGroup = flatSteps[activeIndex]?.groupKey ?? flatSteps[0].groupKey;

  return (
    <Box>
      <StepsTabToggle
        activeGroup={activeGroup}
        onSelect={(groupKey) => scrollToIndex(groupStartIndex[groupKey])}
        sx={{ mb: 3 }}
      />

      <Box
        ref={scrollRef}
        sx={{
          display: "flex",
          gap: 2,
          overflowX: "auto",
          scrollSnapType: "x mandatory",
          px: `${EDGE_PAD_PERCENT}%`,
          // Cards scale up to 1 at centre; without room the transform clips.
          py: 1,
          // The deck owns horizontal gestures, the page keeps vertical ones.
          overscrollBehaviorX: "contain",
          scrollbarWidth: "none",
          "&::-webkit-scrollbar": { display: "none" },
          // Negative margin lets the deck bleed to the screen edges inside
          // SectionContainer's padding, so the peeking card runs off-screen
          // rather than stopping short of it.
          mx: -3,
        }}
      >
        {flatSteps.map((step, index) => (
          <StepDeckCard
            key={`${step.groupKey}-${step.step}`}
            step={step}
            index={index}
            count={flatSteps.length}
            progress={scrollXProgress}
            reducedMotion={prefersReducedMotion}
          />
        ))}
      </Box>

      <Box
        sx={{
          mt: 3,
          height: 4,
          borderRadius: 2,
          bgcolor: landingColors.border,
          overflow: "hidden",
        }}
      >
        <Box
          component={motion.div}
          style={{ scaleX: railScaleX }}
          sx={{
            height: "100%",
            borderRadius: 2,
            bgcolor: landingColors.primary,
            transformOrigin: "left center",
          }}
        />
      </Box>
    </Box>
  );
};

export default MobileStepDeck;
